<!-- Adapted from YesPlayMusic v0.4.11 (MIT), upstream CoverRow.vue. -->
<template>
  <div class="cover-row" :style="rowStyles">
    <div
      v-for="item in items"
      :key="item.id"
      class="item"
      :class="{ artist: type === 'artist' }"
    >
      <Cover
        :id="item.id"
        :artist="type === 'artist' ? item : null"
        :label="item.name"
        :tracks="item.tracks || null"
        :source-filter="sourceFilter"
        :image-url="getImageUrl(item)"
        :type="type"
        :play-button-size="type === 'artist' ? 26 : playButtonSize"
      />
      <div class="text">
        <div v-if="showPlayCount" class="info">
          <span class="play-count"
            ><svg-icon icon-class="play" />{{
              formatPlayCount(item.playCount)
            }}
          </span>
        </div>
        <div class="title" :style="{ fontSize: subTextFontSize }">
          <span v-if="isExplicit(item)" class="explicit-symbol"
            ><ExplicitSymbol
          /></span>
          <span v-if="isPrivacy(item)" class="lock-icon">
            <svg-icon icon-class="lock"
          /></span>
          <router-link v-if="getTitleLink(item)" :to="getTitleLink(item)">{{ item.name }}</router-link><span v-else :title="artistReason(item)">{{ item.name }}</span>
        </div>
        <div v-if="type !== 'artist' && subText !== 'none'" class="info">
          <ArtistsInLine v-if="subText === 'artist'" :artists="itemArtists(item)" /><span v-else>{{ getSubText(item) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Cover from './Cover.vue';
import ExplicitSymbol from './ExplicitSymbol.vue';
import SvgIcon from './SvgIcon.vue';
import { runtime } from '../bridge';
import { artistNavigation } from '../artist-navigation';
import { workspace } from '../workspace';
import ArtistsInLine from './ArtistsInLine.vue';

export default {
  name: 'CoverRow',
  components: {
    Cover,
    ExplicitSymbol,
    SvgIcon,
    ArtistsInLine,
  },
  props: {
    items: { type: Array, required: true },
    sourceFilter: { type: String, default: 'all' },
    type: { type: String, required: true },
    subText: { type: String, default: 'none' },
    subTextFontSize: { type: String, default: '16px' },
    showPlayCount: { type: Boolean, default: false },
    columnNumber: { type: Number, default: 5 },
    gap: { type: String, default: '44px 24px' },
    playButtonSize: { type: Number, default: 22 },
  },
  computed: {
    rowStyles() {
      return {
        'grid-template-columns': `repeat(${this.columnNumber}, 1fr)`,
        gap: this.gap,
      };
    },
  },
  methods: {
    itemArtists(item) { const platform = item.platform || (String(item.id).startsWith('qq:') ? 'tx' : /^\d+$/.test(String(item.id)) ? 'wy' : ''); return (item.artists || (item.artist ? [item.artist] : [])).map(artist => typeof artist === 'string' ? { name: artist, platform } : { platform, ...artist }); },
    formatPlayCount(value) {
      const count = Number(value) || 0;
      if (count >= 100000000) return `${(count / 100000000).toFixed(1)}亿`;
      if (count >= 10000) return `${Math.floor(count / 10000)}万`;
      return count;
    },
    getSubText(item) {
      if (this.subText === 'copywriter') return item.copywriter;
      if (this.subText === 'description') return item.description;
      if (this.subText === 'updateFrequency') return item.updateFrequency;
      if (this.subText === 'creator') return item.creator?.nickname ? 'by ' + item.creator.nickname : '';
      if (this.subText === 'releaseYear')
        return item.publishTime ? new Date(item.publishTime).getFullYear() : '';
      if (this.subText === 'artist') {
        if (item.artist !== undefined) return item.artist.name;
        if (item.artists?.length) return item.artists.map(artist => typeof artist === 'string' ? artist : artist.name).join(' / ');
      }
      if (this.subText === 'albumType+releaseYear') {
        let albumType = item.type;
        if (item.type === 'EP/Single') {
          albumType = item.size === 1 ? 'Single' : 'EP';
        } else if (item.type === 'Single') {
          albumType = 'Single';
        } else if (item.type === '专辑') {
          albumType = 'Album';
        }
        return [albumType, item.publishTime ? new Date(item.publishTime).getFullYear() : ''].filter(Boolean).join(' · ');
      }
      if (this.subText === 'appleMusic') return 'by Apple Music';
    },
    isPrivacy(item) {
      return this.type === 'playlist' && item.privacy === 10;
    },
    isExplicit(item) {
      return this.type === 'album' && (item.mark & 1048576) === 1048576;
    },
    getTitleLink(item) {
      if (this.type === 'artist') return artistNavigation(item, workspace.mode).route;
      return { name: this.type, params: { id: String(item.id) }, query: this.sourceFilter !== 'all' && (this.type === 'playlist' || String(item.id).startsWith('local:')) ? { source: this.sourceFilter } : {} };
    },
    artistReason(item) { return artistNavigation(item, workspace.mode).reason; },
    getImageUrl(item) {
      if (item.img1v1Url) {
        let img1v1ID = item.img1v1Url.split('/');
        img1v1ID = img1v1ID[img1v1ID.length - 1];
        if (img1v1ID === '5639395138885805.jpg') {
          // 没有头像的歌手，网易云返回的img1v1Url并不是正方形的 😅😅😅
          return 'https://p2.music.126.net/VnZiScyynLG7atLIZ2YPkw==/18686200114669622.jpg?param=512y512';
        }
      }
      let img = item.img1v1Url || item.picUrl || item.coverImgUrl;
      if (!img) return runtime.cover();
      img = img.replace(/^http:/, 'https:');
      if (/^https:\/\/[^/]*music\.126\.net\//.test(img)) return `${img}${img.includes('?') ? '&' : '?'}param=512y512`;
      return img;
    },
  },
};
</script>

<style lang="scss" scoped>
.cover-row {
  display: grid;
}

.item {
  color: var(--color-text);
  .text {
    margin-top: 8px;
    .title {
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      word-break: break-all;
    }
    .info {
      font-size: 12px;
      opacity: 0.68;
      line-height: 18px;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      word-break: break-word;
    }
  }
}

.item.artist {
  display: flex;
  flex-direction: column;
  text-align: center;
  .cover {
    display: flex;
  }
  .title {
    margin-top: 4px;
  }
}

@media (max-width: 834px) {
  .item .text .title {
    font-size: 14px;
  }
}

.explicit-symbol {
  opacity: 0.28;
  color: var(--color-text);
  float: right;
  .svg-icon {
    margin-bottom: -3px;
  }
}

.lock-icon {
  opacity: 0.28;
  color: var(--color-text);
  margin-right: 4px;
  // float: right;
  .svg-icon {
    height: 12px;
    width: 12px;
  }
}

.play-count {
  font-weight: 600;
  opacity: 0.58;
  color: var(--color-text);
  font-size: 12px;
  .svg-icon {
    margin-right: 3px;
    height: 8px;
    width: 8px;
  }
}
</style>
